import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { filterConnectedCases } from './mapping/filter-connected-cases';
import { mapBowtieData, mapCaseData } from './mapping/map-bowtie';
import { mapIncludeConnectedCases } from './mapping/map-include-connected-cases';
import { getInspectionEvaluations, mapChecklistEvaluation } from './mapping/map-inspection';
import { api } from './api';
import {
  ClearBowtieData,
  RequestBowtie, ReceiveBowtie, FailBowtie,
  RequestDraft, ReceiveDraft, FailDraft, CleanData,
  RequestConnectDraft, ReceiveConnectDraft, FailConnectDraft,
  RequestCase, ReceiveCase, FailCase,
  RequestConnectedCases, ReceiveConnectedCases, FailConnectedCases, RequestBowtieFavourite, ReceiveBowtieFavourite, IncrementRequestId
} from './action-types';
import { BowtieLayout } from './types';
import { BowtieInstanceState } from 'app/area/bowtie/types-store';

function getBowtieInstanceState(getState: () => ApplicationState, instanceKey: string): BowtieInstanceState {
  return getState().bowtie?.instances?.[instanceKey] || {} as BowtieInstanceState;
}

export const actionCreators = {

  loadBowtie: (
    bowtieLayout: BowtieLayout,
    caseNumber: number,
    connectedDraftId: number,
    instanceKey: string
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const draftMode = isDefinedIncludingZero(connectedDraftId);
      dispatch(new ClearBowtieData(instanceKey));
      dispatch(new RequestBowtie(caseNumber, connectedDraftId, instanceKey));

      if (draftMode) {
        await dispatch(actionCreators.loadDraft(connectedDraftId, instanceKey));
      }

      try {
        const bowtieFavouritePromise = dispatch(actionCreators.createBowtieFavouriteIfNeeded(instanceKey));

        const layoutData = bowtieLayout != null
          ? bowtieLayout
          : await api.loadLayout();

        const {
          draftData
        } = getBowtieInstanceState(getState, instanceKey);

        const includeConnectedCases = !draftMode || mapIncludeConnectedCases(layoutData);

        const draftOriginalCase = draftMode && isDefinedIncludingZero(draftData?.caseNumber)
          ? draftData.caseNumber : undefined;

        await bowtieFavouritePromise;
        const {
          bowtieFavouriteId
        } = getBowtieInstanceState(getState, instanceKey);

        const bowtieData = await api.loadBowtie(caseNumber, includeConnectedCases, draftOriginalCase, bowtieFavouriteId);

        const caseDataModel = mapBowtieData(bowtieData.caseDataModel);
        const evaluations = getInspectionEvaluations(caseDataModel.cases);

        if (caseDataModel.cases.length === 0) {
          dispatch(new FailBowtie(caseNumber, connectedDraftId, instanceKey));
        } else {
          dispatch(new ReceiveBowtie(caseNumber, connectedDraftId, caseDataModel, layoutData,
            bowtieData.caseTemplate, evaluations, instanceKey));
        }

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailBowtie(caseNumber, connectedDraftId, instanceKey));
      }
    },

  loadDraft: (draftId: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const { draftRequestId } = getBowtieInstanceState(getState, instanceKey);
      const requestId = (draftRequestId || 0) + 1;

      dispatch(new RequestDraft(requestId, instanceKey));

      try {
        const [
          draftData,
          connectionData
        ] = await Promise.all([
          api.loadDraft(draftId),
          api.loadDraftConnectionOptions(draftId)
        ]);

        const connectionTypes = connectionData && connectionData.rootCodes &&
          connectionData.rootCodes.map(codeItemToStoreValue);

        dispatch(new ReceiveDraft(requestId, mapCaseData(draftData), connectionTypes, instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDraft(requestId, instanceKey));
      }
    },

  loadEvaluation: (caseNumber: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCase(caseNumber, instanceKey));

      try {

        const {
          bowtieFavouriteId
        } = getBowtieInstanceState(getState, instanceKey);

        const evaluations = await api.loadEvaluation(caseNumber, bowtieFavouriteId);
        const evaluation = evaluations.find(e => e.caseId === caseNumber);

        dispatch(new ReceiveCase(caseNumber, mapChecklistEvaluation(evaluation), instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCase(caseNumber, instanceKey));
      }
    },

  loadConnectedCases: (caseNumber: number, connectionType: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestConnectedCases(caseNumber, connectionType, instanceKey));

      try {

        const {
          bowtieFavouriteId,
          caseDataModel
        } = getBowtieInstanceState(getState, instanceKey);

        const connectedCases = await api.loadConnectedCases([caseNumber], [connectionType], bowtieFavouriteId);

        dispatch(new ReceiveConnectedCases(caseNumber, connectionType,
          filterConnectedCases(caseNumber, connectedCases, caseDataModel), instanceKey
        ));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectedCases(caseNumber, connectionType, instanceKey));
      }
    },

  clean: (instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new CleanData(instanceKey));
    },

  addConnection: (draftId: number, caseNumber: number, connectionTypeId: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestConnectDraft(caseNumber, instanceKey));

        await api.addDraftConnection(draftId, caseNumber, connectionTypeId);

        dispatch(new ReceiveConnectDraft(caseNumber, instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectDraft(caseNumber, instanceKey));
      }

      dispatch(actionCreators.loadDraft(draftId, instanceKey));
    },

  removeConnection: (draftId: number, caseNumber: number, connectionTypeId: number, instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestConnectDraft(caseNumber, instanceKey));

        await api.removeDraftConnection(draftId, caseNumber, connectionTypeId);

        dispatch(new ReceiveConnectDraft(caseNumber, instanceKey));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectDraft(caseNumber, instanceKey));
      }

      dispatch(actionCreators.loadDraft(draftId, instanceKey));
    },

  createBowtieFavouriteIfNeeded: (instanceKey: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const {
          bowtieFavouriteId
        } = getBowtieInstanceState(getState, instanceKey);

        if (!bowtieFavouriteId) {
          dispatch(new RequestBowtieFavourite(instanceKey));
          const utcOffset = getState().app.settings.utcOffset;
          const newBowtieFavouriteId = await api.createBowtieFavourite({ utcOffset });
          dispatch(new ReceiveBowtieFavourite(newBowtieFavouriteId, instanceKey));
        }
      }
      catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  incrementRequestId: (instanceKey: string): ActionDispatching<any, any> =>
    (dispatch: any) => {
      dispatch(new IncrementRequestId(instanceKey));
    }
};
